import { useState, useCallback } from 'react';
import { solarData } from '../components/Quote/SolarData';
import { FinanceData } from '../components/Quote/FinanceData';
import { FOX_MIN_BATTERIES } from '../components/Quote/Promotions';

const useHardware = () => {
    const initialHardwareState = {
        // Solar and storage, by default. This used to start empty and be filled
        // in by the product picker on the Locate page, which is gone — but it is
        // still the `trades` the estimate is requested with, and the EV charger
        // control adds 3 itself when it leaves zero.
        solutions: [1, 2],
        module: solarData.modules.options[0],
        inverter: solarData.inverters.options[0],
        systemSize: 0,
        offset: 100,
        kWh: 0,
        unit: '',
        battery: solarData.batteries.options[0],
        // Five FoxESS batteries out of the gate.
        //
        // The default module and inverter are already the FoxESS package's, so
        // this is the last piece needed for that promotion to be earned the
        // moment the Design page opens — a customer sees $4,000 off their own
        // system rather than a list of conditions to go and satisfy. It still
        // depends on the array clearing 10.5 kW, which comes from their bill,
        // and every one of these is a slider away from being changed.
        batteryQuantity: FOX_MIN_BATTERIES,
        backupHours: 0,
        recommendedBattQuantity: 0,
        // False, not true: the pricing effect overwrites the battery count with
        // its own recommendation while this is set, which would undo the five
        // above before the page finished its first render.
        batteryState: false,
        powerwallCost: 0,
        additionalPowerwallCost: 8500,
        capacity: 0,
        services: solarData.services.options[0],
        serviceQuantity: 0,
        solarPPW: 0,
        solarCost: 0,
        batteryCost: 0,
        serviceCost: 0,
        totalCost: 0,
        batteryCapacity: 0,
        // Which package card is highlighted. Only a starting point for the
        // hardware — the promotion itself is earned by the build, so this never
        // decides what the customer pays.
        selectedPackage: 'independence',
        promotion: null,
        promotionAmount: 0,
        totalAfterPromotion: 0,
        downPayment: 0,
        financedTotal: 0,
        financedMonthlyPayment: 0,
        // Legacy names, kept in sync with the promotion figures above. The
        // backend still reads these off the hardware blob for the rep deal email
        // (config/nodeMailer.js) and the design link (estimateController). They
        // go when that side is renamed; until then, dropping them would blank
        // out those rows.
        taxCredit: 0,
        totalAfterTaxCredit: 0,
        financedMonthlyPaymentPreTaxCredit: 0,
        financedMonthlyPaymentAfterTaxCredit: 0,
        selectedLender: Object.keys(FinanceData.Lenders[0])[0], 
        selectedProduct: FinanceData.Lenders[0][Object.keys(FinanceData.Lenders[0])[0]][0],
        paymentType: '',
        satelliteImage: null,
        designInfo: null,
        designImage: null,
        designText: 'Loading Home...',
        targetEnergyAnnual: 0,
        // The array size the customer asked for, before it is rounded to whole
        // panels. Module-independent, so the package cards can price against a
        // fixed target instead of one that moves with the selected panel.
        targetWatts: 0,
        annualProduction: 0
    }

    const [hardware, setHardware] = useState(initialHardwareState);

    const updateHardware = useCallback((field, value) => {
        setHardware((prevState) => ({
          ...prevState,
          [field]: typeof value === 'function' ? value(prevState[field]) : value,
        }));
    }, []);

    const updateLender = useCallback((lender) => {
        const newSelectedLender = FinanceData.Lenders.find((l) => l[lender]);
        const newProducts = newSelectedLender ? newSelectedLender[lender] : [];
        const newSelectedProduct = newProducts[0] || {};
    
        setHardware((prevHardware) => ({
          ...prevHardware,
          selectedLender: lender,
          selectedProduct: newSelectedProduct,
        }));
    }, []);

    // Function to update selected product
  const updateProduct = useCallback((product) => {
    const lenderProducts = FinanceData.Lenders.find((l) => l[hardware.selectedLender])[hardware.selectedLender];
    const newSelectedProduct = lenderProducts.find((productData) => productData.product === product);

    setHardware((prevHardware) => ({
      ...prevHardware,
      selectedProduct: newSelectedProduct,
    }));
  }, [hardware.selectedLender]);

  // Function to add or remove a value from the solutions array
  const updateSolutions = useCallback((value) => {
    setHardware((prevHardware) => {
      const isValueInSolutions = prevHardware.solutions.includes(value);

      return {
        ...prevHardware,
        solutions: isValueInSolutions
          ? prevHardware.solutions.filter((item) => item !== value) // Remove value
          : [...prevHardware.solutions, value], // Add value
      };
    });
  }, []);

  // Function to reset hardware to its initial state
  const resetHardwareState = useCallback(() => {
    setHardware(initialHardwareState); // Reset hardware state to its initial values
  }, []);

  return { hardware, updateHardware, updateLender, updateProduct, updateSolutions, resetHardwareState };
}

export default useHardware;